/* eslint-disable monorepo/no-internal-import */
import React, {ComponentType, useEffect} from 'react';
import {
  markComponentMounted,
  markComponentUnmounted,
  registerComponent,
} from './HydrationProvider';
import {useAddToResourceHints} from './ResourceHintsProvider';

let failedLazyImportsRetryGeneration = 0;

/**
 * `React.lazy` keeps a rejected import forever, so a component whose chunk
 * failed to load would throw the same error on every remount. After this call,
 * lazy components whose import failed import again the next time they render.
 */
export function retryFailedLazyImports() {
  failedLazyImportsRetryGeneration++;
}

/**
 * The failed `React.lazy` instance is only replaced once retrying is allowed.
 * React can render the lazy component again before its error reaches the
 * error boundary (e.g. when it suspended inside a parent Suspense boundary),
 * and a fresh instance at that point would swallow the error and import again,
 * endlessly.
 */
function createRetryableLazy<T extends ComponentType<any>>(
  importer: () => Promise<{default: T}>,
) {
  const createInstance = () => {
    const instance = {
      failed: false,
      retryGeneration: failedLazyImportsRetryGeneration,
      Component: React.lazy(() =>
        importer().catch((error: unknown) => {
          instance.failed = true;
          throw error;
        }),
      ),
    };
    return instance;
  };

  let current = createInstance();

  return () => {
    if (
      current.failed &&
      current.retryGeneration !== failedLazyImportsRetryGeneration
    ) {
      current = createInstance();
    }
    return current.Component;
  };
}

export function lazy<T extends ComponentType<any>>(
  importer: () => Promise<{default: T}>,
  config: {
    /**
     * If true, the lazy-loaded module will be optimized for preloading as a priority resource.
     * Usually, this is only necessary for main Page/Route components.
     * Other modules should be downloaded lazily by react on demand when hydrating the page.
     */
    aboveTheFold?: boolean;
    /**
     * If true, the lazy-loaded module will be treated as an entry point that needs hydration.
     * The useHydrationDone hook will return false until the module has been hydrated on the client.
     * Set this flag to true for components that are Server-Side Rendered, for example, a SSRed Page component.
     *
     * PS: Do not set this to true for components that are only rendered on the client side (e.g. modals, dropdowns, components which are rendered on when inView etc)
     */
    isSsrAndNeedsHydration?: boolean;
  } & (
    | {
        /**
         * The fallback component to show while the lazy-loaded module is loading.
         * If not provided, the Suspense Boundary will render nothing until the module is loaded.
         */
        fallback?: NonNullable<React.ReactNode> | null;
      }
    | {
        /**
         * If true, the Suspense Boundary will be skipped and the module will still be loaded lazily but would not suspend the rendering.
         */
        skipSuspense?: true;
      }
  ) = {},
) {
  const {isSsrAndNeedsHydration, aboveTheFold} = config;

  const getLazyLoaded = createRetryableLazy(importer);

  const componentPath =
    importer.toString().match(/import\(['"](.+?)['"]\)/)?.[1] || '';
  const componentName = componentPath.split('/').pop() || '';
  const componentId = `${componentName}`;

  const Suspensified = ({ref, ...props}: {ref?: React.Ref<unknown>}) => {
    const LazyLoaded = getLazyLoaded();

    useEffect(() => {
      if (!isSsrAndNeedsHydration) return;
      markComponentMounted(componentId);
      return () => markComponentUnmounted(componentId);
    }, []);

    if (!process.browser && aboveTheFold) {
      // eslint-disable-next-line react-hooks/rules-of-hooks -- its okay break the rules here because the config.aboveTheFold is a constant, would never change for a given component
      useAddToResourceHints(importer.toString());
    }

    if (config && 'skipSuspense' in config && config.skipSuspense) {
      return <LazyLoaded {...(props as any)} ref={ref} />;
    }

    if (!process.browser) {
      return (
        <React.Suspense fallback={null}>
          <LazyLoaded {...(props as any)} ref={ref} />
        </React.Suspense>
      );
    }

    if (process.browser && isSsrAndNeedsHydration) {
      registerComponent(componentId);
    }

    const LazyMarkHydrationDone = getLazyMarkHydrationDone();

    return (
      <React.Suspense
        fallback={config && 'fallback' in config ? config.fallback : null}
      >
        {isSsrAndNeedsHydration && (
          <LazyMarkHydrationDone componentId={componentId} />
        )}

        <LazyLoaded {...(props as any)} ref={ref} />
      </React.Suspense>
    );
  };

  Suspensified.preload = importer;

  return Suspensified as unknown as T & {preload: () => Promise<void>};
}

/**
 * Lazy load this too to be like the main component, which is lazy loaded as well.
 */
const getLazyMarkHydrationDone = createRetryableLazy(() =>
  import('./MarkHydrationDone').then(mod => ({
    default: mod.MarkHydrationDone,
  })),
);
